
import { useState, useMemo } from 'react';

interface Slot {
  id: string;
  date: string;
  time_slot: string;
  is_available: boolean;
}

interface AvailabilityManagerProps {
  slots: Slot[];
  onAddSlot: (date: string, time: string) => void;
  onRemoveSlot: (id: string) => void;
  onToggleSlot: (id: string, available: boolean) => void;
}

const TIME_OPTIONS = [
  '08:00', '09:00', '10:00', '11:00', '12:00',
  '13:00', '14:00', '15:00', '16:00', '17:00',
];

export default function AvailabilityManager({
  slots,
  onAddSlot,
  onRemoveSlot,
  onToggleSlot,
}: AvailabilityManagerProps) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [newTime, setNewTime] = useState('09:00');
  const [showAddForm, setShowAddForm] = useState(false);

  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  ).getDate();

  const firstDayOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  ).getDay();

  const monthName = currentMonth.toLocaleString('default', {
    month: 'long',
    year: 'numeric',
  });

  const slotsByDate = useMemo(() => {
    const map: Record<string, Slot[]> = {};
    slots.forEach((s) => {
      if (!map[s.date]) map[s.date] = [];
      map[s.date].push(s);
    });
    return map;
  }, [slots]);

  const slotsForSelected = useMemo(() => {
    if (!selectedDate) return [];
    return (slotsByDate[selectedDate] || []).sort((a, b) =>
      a.time_slot.localeCompare(b.time_slot)
    );
  }, [slotsByDate, selectedDate]);

  const existingTimes = useMemo(() => {
    if (!selectedDate) return new Set<string>();
    return new Set(slotsForSelected.map((s) => s.time_slot.substring(0, 5)));
  }, [slotsForSelected, selectedDate]);

  const availableTimes = TIME_OPTIONS.filter((t) => !existingTimes.has(t));

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${minutes} ${ampm}`;
  };

  const handleAddSlot = () => {
    if (!selectedDate || !newTime) return;
    onAddSlot(selectedDate, newTime + ':00');
    setShowAddForm(false);
    if (availableTimes.length > 1) {
      const nextAvailable = availableTimes.find((t) => t !== newTime);
      if (nextAvailable) setNewTime(nextAvailable);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Calendar */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() =>
              setCurrentMonth(
                new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
              )
            }
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg text-slate-700"></i>
          </button>
          <h4 className="text-base font-semibold text-slate-900">{monthName}</h4>
          <button
            onClick={() =>
              setCurrentMonth(
                new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
              )
            }
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg text-slate-700"></i>
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
            <div key={d} className="text-center text-[11px] font-semibold text-gray-400 py-1.5">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDayOfMonth }).map((_, i) => (
            <div key={`e-${i}`} className="h-9" />
          ))}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dateStr = `${currentMonth.getFullYear()}-${String(
              currentMonth.getMonth() + 1
            ).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dateObj = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
            const isPast = dateObj < today;
            const hasSlots = !!slotsByDate[dateStr]?.length;
            const isSelected = selectedDate === dateStr;

            return (
              <button
                key={day}
                disabled={isPast}
                onClick={() => {
                  setSelectedDate(dateStr);
                  setShowAddForm(false);
                }}
                className={`h-9 rounded-lg text-sm font-medium transition-all cursor-pointer relative
                  ${isSelected
                    ? 'bg-emerald-500 text-white'
                    : isPast
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-slate-700 hover:bg-emerald-50'
                  }
                `}
              >
                {day}
                {hasSlots && !isSelected && (
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-emerald-500"></span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
            <span className="text-[11px] text-gray-500">Has time slots</span>
          </div>
        </div>
      </div>

      {/* Time Slots Manager */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        {!selectedDate ? (
          <div className="flex flex-col items-center justify-center h-full py-16">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <i className="ri-calendar-event-line text-2xl text-gray-400"></i>
            </div>
            <p className="text-sm text-gray-500 font-medium">Select a date</p>
            <p className="text-xs text-gray-400 mt-1">Click on a date to manage time slots</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h4 className="text-base font-bold text-slate-900">
                  {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                  })}
                </h4>
                <p className="text-xs text-gray-500 mt-0.5">
                  {slotsForSelected.length} time slot{slotsForSelected.length !== 1 ? 's' : ''}
                </p>
              </div>
              {availableTimes.length > 0 && (
                <button
                  onClick={() => {
                    setShowAddForm(!showAddForm);
                    setNewTime(availableTimes[0]);
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 text-white text-xs font-medium rounded-lg hover:bg-emerald-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line text-sm"></i>
                  Add Slot
                </button>
              )}
            </div>

            {showAddForm && (
              <div className="bg-emerald-50 rounded-xl p-4 mb-4 border border-emerald-100">
                <p className="text-xs font-semibold text-emerald-800 mb-3">Add New Time Slot</p>
                <div className="flex items-center gap-3">
                  <select
                    value={newTime}
                    onChange={(e) => setNewTime(e.target.value)}
                    className="flex-1 px-3 py-2 rounded-lg border border-emerald-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
                  >
                    {availableTimes.map((t) => (
                      <option key={t} value={t}>
                        {formatTime(t)}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleAddSlot}
                    className="px-4 py-2 bg-emerald-500 text-white text-xs font-medium rounded-lg hover:bg-emerald-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => setShowAddForm(false)}
                    className="px-3 py-2 text-gray-500 text-xs font-medium rounded-lg hover:bg-white transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {slotsForSelected.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-sm text-gray-500">No time slots for this date</p>
                <p className="text-xs text-gray-400 mt-1">Add slots to make this date available for booking</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {slotsForSelected.map((slot) => (
                  <div
                    key={slot.id}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                      slot.is_available
                        ? 'bg-white border-gray-100'
                        : 'bg-gray-50 border-gray-100 opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        slot.is_available ? 'bg-emerald-100' : 'bg-gray-200'
                      }`}>
                        <i className={`ri-time-line text-sm ${
                          slot.is_available ? 'text-emerald-600' : 'text-gray-500'
                        }`}></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          {formatTime(slot.time_slot.substring(0, 5))}
                        </p>
                        <p className="text-[11px] text-gray-500">
                          {slot.is_available ? 'Available' : 'Disabled'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onToggleSlot(slot.id, !slot.is_available)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                          slot.is_available
                            ? 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                            : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                        }`}
                        title={slot.is_available ? 'Disable' : 'Enable'}
                      >
                        <i className={`${slot.is_available ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                      </button>
                      <button
                        onClick={() => onRemoveSlot(slot.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors cursor-pointer"
                        title="Delete"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
